import { z } from 'zod';
import { ERROR_MESSAGES } from '@/lib/utils/constants';
import { LOBLevel, TaxType } from '@/types';

/**
 * Schemas de validación para datos maestros
 */

export const currencySchema = z.object({
  code: z
    .string()
    .min(3, ERROR_MESSAGES.MIN_LENGTH(3))
    .max(3, ERROR_MESSAGES.MAX_LENGTH(3))
    .toUpperCase(),
  name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
  symbol: z.string().min(1, ERROR_MESSAGES.REQUIRED).max(5, ERROR_MESSAGES.MAX_LENGTH(5)),
  exchangeRate: z
    .number()
    .positive('La tasa de cambio debe ser positiva')
    .min(0.0001, 'La tasa de cambio es muy baja'),
  isBase: z.boolean().optional(),
  sapCode: z.string().max(64).optional().nullable(),
});

export const lobSchema = z.object({
  name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
  code: z
    .string()
    .min(2, ERROR_MESSAGES.MIN_LENGTH(2))
    .max(50, ERROR_MESSAGES.MAX_LENGTH(50))
    .toUpperCase(),
  level: z.nativeEnum(LOBLevel),
  parentId: z.string().optional(),
  description: z.string().max(500, ERROR_MESSAGES.MAX_LENGTH(500)).optional(),
  targetMarginPercentage: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).max(100).optional()
  ),
  floorMarginPercentage: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).max(100).optional()
  ),
  // Pricer Datec — solo se setean en level=3 (Marca)
  pctGarantia: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).max(100).optional()
  ),
  pctNacionalizacion: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).max(100).optional()
  ),
  // Flete de la marca. Las claves son las de TIPOS_FLETE del backend.
  tipoFlete: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z
      .enum([
        'NINGUNO',
        'AEREO',
        'MARITIMO',
        'TERRESTRE',
        'AEREO_1',
        'AEREO_2',
        'MARITIMO_1',
        'MARITIMO_2',
        'MANUAL_1',
        'MANUAL_2',
        'FIJO_NWS',
      ])
      .optional()
  ),
  pctFlete: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().min(0).max(100).optional()
  ),
  sapCode: z.string().max(64).optional().nullable(),
});

export const taxConfigSchema = z.object({
  name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
  code: z
    .string()
    .min(2, ERROR_MESSAGES.MIN_LENGTH(2))
    .max(50, ERROR_MESSAGES.MAX_LENGTH(50))
    .toUpperCase(),
  taxType: z.nativeEnum(TaxType),
  rate: z
    .number()
    .min(0, 'La tasa no puede ser negativa')
    .max(100, 'La tasa no puede exceder 100%'),
  description: z.string().max(500, ERROR_MESSAGES.MAX_LENGTH(500)).optional(),
  region: z.string().max(100, ERROR_MESSAGES.MAX_LENGTH(100)).optional(),
});

export const rateCardSchema = z
  .object({
    name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
    lobId: z.string().min(1, ERROR_MESSAGES.REQUIRED),
    currencyId: z.string().min(1, ERROR_MESSAGES.REQUIRED),
    baseRate: z.number().positive('La tarifa base debe ser positiva'),
    description: z.string().max(500, ERROR_MESSAGES.MAX_LENGTH(500)).optional(),
    validFrom: z.date(),
    validTo: z.date().optional(),
  })
  .refine(
    (data) => {
      if (data.validTo) {
        return data.validTo > data.validFrom;
      }
      return true;
    },
    {
      message: 'La fecha de fin debe ser posterior a la fecha de inicio',
      path: ['validTo'],
    }
  );

export const countrySchema = z.object({
  name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
  code: z.string().length(2, 'El código ISO debe tener 2 caracteres').toUpperCase(),
  sapCode: z.string().max(64).optional().nullable(),
});

export const quoteTypeSchema = z.object({
  code: z
    .string()
    .min(2, ERROR_MESSAGES.MIN_LENGTH(2))
    .max(50, ERROR_MESSAGES.MAX_LENGTH(50))
    .toUpperCase(),
  name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
  description: z.string().max(500, ERROR_MESSAGES.MAX_LENGTH(500)).optional(),
  order: z.coerce.number().int().min(0, 'El orden debe ser mayor o igual a 0').optional(),
  codePrefix: z
    .string()
    .min(1, ERROR_MESSAGES.MIN_LENGTH(1))
    .max(10, ERROR_MESSAGES.MAX_LENGTH(10))
    .toUpperCase(),
  upstreamTypeId: z
    .string()
    .optional()
    .nullable()
    .transform((v) => v || null),
  upstreamVariableCode: z
    .string()
    .max(100, ERROR_MESSAGES.MAX_LENGTH(100))
    .optional()
    .nullable()
    .transform((v) => v || null),
  sapCode: z.string().max(64).optional().nullable(),
});

export type QuoteTypeFormData = z.infer<typeof quoteTypeSchema>;
export type CurrencyFormData = z.infer<typeof currencySchema>;
export type LOBFormData = z.infer<typeof lobSchema>;
export type TaxConfigFormData = z.infer<typeof taxConfigSchema>;
export type RateCardFormData = z.infer<typeof rateCardSchema>;
export type CountryFormData = z.infer<typeof countrySchema>;
